import { useEffect, useRef, useState } from "react"
import { cn } from "cn"
import Markdown, { type Components } from "react-markdown"

import { AriaButton } from "./ui/aria-button"
import { Button } from "./ui/button"

// Estimated button heights for deciding whether showing the button is worthwhile.
// Only show the button if the text overflow exceeds this height, otherwise the
// button itself takes up more space than we save by clamping.
const BUTTON_HEIGHT_ESTIMATE = 56 // mt-4 (16px) + button (40px)
const MINIMAL_BUTTON_HEIGHT_ESTIMATE = 24 // just underlined text

export function ClampedText({
  numberOfLines,
  content,
  isMarkdown,
  minimal,
  className,
  onReadMore,
  hideReadLess,
  onToggle,
  disableHeadings,
  markdownComponents,
}: {
  content: string
  numberOfLines: number
  isMarkdown?: boolean
  minimal?: boolean
  className?: string
  onReadMore?: () => void
  onToggle?: (isExpanded: boolean) => void
  hideReadLess?: boolean
  disableHeadings?: boolean
  markdownComponents?: Components
}) {
  const [isTruncated, setIsTruncated] = useState(true)
  const [showReadMore, setShowReadMore] = useState(false)
  const textRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!textRef.current) return

    const compareHeights = () => {
      if (!textRef.current) return

      const overflow =
        textRef.current.scrollHeight - textRef.current.clientHeight
      const buttonHeight = minimal
        ? MINIMAL_BUTTON_HEIGHT_ESTIMATE
        : BUTTON_HEIGHT_ESTIMATE

      // Only show the button if the overflow exceeds the button height.
      // Otherwise, the text would fit if we just didn't have the button.
      const shouldShowButton = overflow > buttonHeight
      setShowReadMore(shouldShowButton)

      // If there's some overflow but not enough to warrant a button,
      // don't truncate at all - just show the full text.
      if (!shouldShowButton && overflow > 0) {
        setIsTruncated(false)
      }
    }

    compareHeights()
  }, [numberOfLines, content, minimal])

  const toggleTruncate = () => {
    setIsTruncated(!isTruncated)
    onToggle?.(isTruncated)
  }

  const classNames = cn("prose text-pretty", className)
  const buttonText = isTruncated ? "Read more" : "Read less"
  const buttonAction = onReadMore || toggleTruncate

  const shouldShowButton = showReadMore && (isTruncated || !hideReadLess)

  return (
    <>
      <div
        ref={textRef}
        className={!isMarkdown ? classNames : undefined}
        style={
          isTruncated
            ? {
                overflow: "hidden",
                display: "-webkit-box",
                WebkitLineClamp: numberOfLines,
                WebkitBoxOrient: "vertical",
              }
            : undefined
        }
      >
        {isMarkdown ? (
          <div className={classNames}>
            <Markdown
              components={{
                code: ({ children }) => <>{children}</>,
                pre: ({ children }) => <>{children}</>,
                ...(disableHeadings && {
                  h1: ({ children }) => <p>{children}</p>,
                  h2: ({ children }) => <p>{children}</p>,
                  h3: ({ children }) => <p>{children}</p>,
                  h4: ({ children }) => <p>{children}</p>,
                  h5: ({ children }) => <p>{children}</p>,
                  h6: ({ children }) => <p>{children}</p>,
                }),
                ...markdownComponents,
              }}
            >
              {content}
            </Markdown>
          </div>
        ) : (
          content
        )}
      </div>
      {shouldShowButton &&
        (minimal ? (
          <div>
            <AriaButton onClick={buttonAction} className="-m-1 p-1 underline">
              {buttonText}
            </AriaButton>
          </div>
        ) : (
          <div className="mt-4">
            <Button onClick={buttonAction} variant="outline" size="md">
              {buttonText}
            </Button>
          </div>
        ))}
    </>
  )
}
